import React, { useState, useEffect } from 'react';
import { Container, Row, Col, Card, Button, Form, Spinner, Alert, Table, Modal } from 'react-bootstrap';
import SelectControl from './common/SelectControl';
import DarkHeader from './common/DarkHeader';

function NetworkConsumptionReport({ onBack, token, onLogout, onSettings }) {
    const [groups, setGroups] = useState([]);
    const [selectedGroup, setSelectedGroup] = useState('');
    const [stats, setStats] = useState(null);
    const [experts, setExperts] = useState([]);
    const [selectedExpert, setSelectedExpert] = useState('');
    const [selectedStatus, setSelectedStatus] = useState('3'); // Default: All
    const [dateFrom, setDateFrom] = useState('');
    const [dateTo, setDateTo] = useState('');
    const [bookings, setBookings] = useState([]);
    const [currentPage, setCurrentPage] = useState(0);
    const [hasMore, setHasMore] = useState(false);
    const [hasSearched, setHasSearched] = useState(false);
    
    const [loadingGroups, setLoadingGroups] = useState(false);
    const [loadingStats, setLoadingStats] = useState(false);
    const [loadingExperts, setLoadingExperts] = useState(false);
    const [loadingBookings, setLoadingBookings] = useState(false);
    const [error, setError] = useState(null);
    
    // Modal state
    const [showModal, setShowModal] = useState(false);
    const [modalDescription, setModalDescription] = useState('');
    const [modalTitle, setModalTitle] = useState('');

    // Fetch groups on component mount
    useEffect(() => {
        fetchGroups();
    }, []);

    const fetchGroups = async () => {
        setLoadingGroups(true);
        setError(null);
        
        try {
            const response = await fetch('/api/reports/network-consumption.php?action=groups', {
                headers: {
                    'Authorization': `Bearer ${token}`,
                },
            });

            if (!response.ok) {
                throw new Error('Failed to fetch groups');
            }

            const data = await response.json();
            setGroups(data.groups || []);
            
            // Auto-select if only one group
            if (data.groups && data.groups.length === 1) {
                const groupId = data.groups[0].id.toString();
                setSelectedGroup(groupId);
                fetchStats(groupId);
                fetchExperts(groupId);
            }
        } catch (err) {
            setError('Failed to load groups: ' + err.message);
        } finally {
            setLoadingGroups(false);
        }
    };

    const fetchStats = async (groupId) => {
        setLoadingStats(true);
        setError(null);
        
        try {
            const response = await fetch(`/api/reports/network-consumption.php?action=stats&group=${groupId}`, {
                headers: {
                    'Authorization': `Bearer ${token}`,
                },
            });

            if (!response.ok) {
                throw new Error('Failed to fetch stats');
            }

            const data = await response.json();
            setStats(data.stats || null);
        } catch (err) {
            setError('Failed to load statistics: ' + err.message);
            setStats(null);
        } finally {
            setLoadingStats(false);
        }
    };

    const fetchExperts = async (groupId) => {
        setLoadingExperts(true);
        
        try {
            const response = await fetch(`/api/reports/network-consumption.php?action=experts&group=${groupId}`, {
                headers: {
                    'Authorization': `Bearer ${token}`,
                },
            });

            if (!response.ok) {
                throw new Error('Failed to fetch experts');
            }

            const data = await response.json();
            setExperts(data.experts || []);
        } catch (err) {
            setExperts([]);
        } finally {
            setLoadingExperts(false);
        }
    };

    const fetchBookings = async (append = false) => {
        setLoadingBookings(true);
        setError(null);
        
        try {
            const nextPage = append ? currentPage + 1 : 1;
            
            // Convert date format from YYYY-MM-DD to Ymd
            const formatDate = (dateStr) => {
                if (!dateStr) return '';
                return dateStr.replace(/-/g, ''); // Remove dashes
            };
            
            const params = new URLSearchParams({
                action: 'bookings',
                group: selectedGroup,
                expert: selectedExpert || '',
                status: selectedStatus,
                from: formatDate(dateFrom),
                to: formatDate(dateTo),
                page: nextPage.toString()
            });

            const response = await fetch(`/api/reports/network-consumption.php?${params}`, {
                headers: {
                    'Authorization': `Bearer ${token}`,
                },
            });

            if (!response.ok) {
                throw new Error('Failed to fetch bookings');
            }

            const data = await response.json();
            
            if (append) {
                setBookings(prevBookings => [...prevBookings, ...(data.bookings || [])]);
                setCurrentPage(nextPage);
            } else {
                setBookings(data.bookings || []);
                setCurrentPage(nextPage);
            }
            
            setHasMore(data.has_more || false);
        } catch (err) {
            setError('Failed to load bookings: ' + err.message);
        } finally {
            setLoadingBookings(false);
        }
    };

    const handleGroupChange = (e) => {
        const groupId = e.target.value;
        setSelectedGroup(groupId);
        setStats(null);
        setExperts([]);
        setSelectedExpert('');
        setBookings([]);
        setCurrentPage(0);
        setHasSearched(false);
        
        if (groupId) {
            fetchStats(groupId);
            fetchExperts(groupId);
        }
    };

    const handleSearchClick = () => {
        if (selectedGroup) {
            setBookings([]);
            setCurrentPage(0);
            setHasSearched(true);
            fetchBookings(false);
        }
    };

    const handleLoadMore = () => {
        fetchBookings(true);
    };

    const handleViewDescription = (booking) => {
        setModalTitle(booking.title);
        setModalDescription(booking.description || 'No description available.');
        setShowModal(true);
    };

    const handleCloseModal = () => {
        setShowModal(false);
        setModalTitle('');
        setModalDescription('');
    };

    const setQuickDate = (months) => {
        const today = new Date();
        const to = today.toISOString().split('T')[0];
        
        let from;
        if (months === 0) { // This month
            from = new Date(today.getFullYear(), today.getMonth(), 1).toISOString().split('T')[0];
        } else if (months === 1) { // Last month
            const lastMonth = new Date(today.getFullYear(), today.getMonth() - 1, 1);
            from = lastMonth.toISOString().split('T')[0];
            const lastDay = new Date(today.getFullYear(), today.getMonth(), 0);
            setDateTo(lastDay.toISOString().split('T')[0]);
            setDateFrom(from);
            return;
        } else if (months === 3) { // Last 3 months
            from = new Date(today.getFullYear(), today.getMonth() - 3, 1).toISOString().split('T')[0];
        }
        
        setDateFrom(from);
        setDateTo(to);
    };

    return (
        <div className="page-container">
            {/* Background blur effects */}
            <div className="bg-blur-indigo"></div>
            <div className="bg-blur-fuchsia"></div>
            
            <DarkHeader
                title="Network Consumption Report"
                subtitle="View credit/unit report"
                showBackButton={true}
                onBack={onBack}
                backButtonText="Back to My Reports"
            />

            <Container fluid style={{ position: 'relative', zIndex: 10 }}>
                
                {error && (
                    <Alert variant="danger" className="mb-4">
                        {error}
                    </Alert>
                )}

                {/* Group Selection */}
                <Card className="mb-4">
                    <Card.Body>
                        <h4 className="mb-3">Select group</h4>
                        
                        {loadingGroups ? (
                            <div className="text-center">
                                <Spinner animation="border" size="sm" className="me-2" />
                                Loading groups...
                            </div>
                        ) : (
                            <SelectControl
                                value={selectedGroup}
                                onChange={(val) => handleGroupChange({ target: { value: val } })}
                                options={[
                                    { value: '', label: 'Select group...' },
                                    ...groups.map(group => ({
                                        value: String(group.id),
                                        label: `${group.name}${group.company_name ? ' - ' + group.company_name : ''}`
                                    }))
                                ]}
                                isClearable={false}
                            />
                        )}
                    </Card.Body>
                </Card>

                {/* Unit Stats */}
                {selectedGroup && stats && (
                    <Card className="mb-4">
                        <Card.Body>
                            <h5 className="mb-3">Group: {groups.find(g => g.id.toString() === selectedGroup)?.name}</h5>
                            <p className="mb-0">
                                Available: {stats.available} | Allocated: {stats.allocated} | Consumed: {stats.consumed}
                            </p>
                        </Card.Body>
                    </Card>
                )}

                {/* Filters */}
                {selectedGroup && (
                    <Card className="mb-4">
                        <Card.Body>
                            {/* Expert Filter */}
                            {experts.length > 0 && (
                                <Form.Group className="mb-3">
                                    <Form.Label>Select expert (optional)</Form.Label>
                                    <SelectControl
                                        value={selectedExpert}
                                        onChange={setSelectedExpert}
                                        options={[
                                            { value: '', label: 'Select expert...' },
                                            ...experts.map(expert => ({ value: String(expert.id), label: expert.name }))
                                        ]}
                                    />
                                </Form.Group>
                            )}

                            {/* Credit Status */}
                            <Form.Group className="mb-3">
                                <Form.Label>Credit status</Form.Label>
                                <SelectControl
                                    value={selectedStatus}
                                    onChange={setSelectedStatus}
                                    options={[
                                        { value: '3', label: 'All' },
                                        { value: '1', label: 'Consumed' },
                                        { value: '2', label: 'Allocated' },
                                        { value: '4', label: 'Received' }
                                    ]}
                                />
                            </Form.Group>

                            {/* Quick Date Selectors */}
                            <Form.Group className="mb-3">
                                <div>
                                    <Form.Check 
                                        inline 
                                        type="radio" 
                                        name="quickDate" 
                                        label="This Month" 
                                        onChange={() => setQuickDate(0)}
                                    />
                                    <Form.Check 
                                        inline 
                                        type="radio" 
                                        name="quickDate" 
                                        label="Last Month" 
                                        onChange={() => setQuickDate(1)}
                                    />
                                    <Form.Check 
                                        inline 
                                        type="radio" 
                                        name="quickDate" 
                                        label="Last Three Months" 
                                        onChange={() => setQuickDate(3)}
                                    />
                                </div>
                            </Form.Group>

                            {/* Date Range */}
                            <Row>
                                <Col md={6}>
                                    <Form.Group className="mb-3">
                                        <Form.Label>From date.</Form.Label>
                                        <Form.Control 
                                            type="date" 
                                            value={dateFrom}
                                            onChange={(e) => setDateFrom(e.target.value)}
                                        />
                                    </Form.Group>
                                </Col>
                                <Col md={6}>
                                    <Form.Group className="mb-3">
                                        <Form.Label>To date.</Form.Label>
                                        <Form.Control 
                                            type="date" 
                                            value={dateTo}
                                            onChange={(e) => setDateTo(e.target.value)}
                                        />
                                    </Form.Group>
                                </Col>
                            </Row>

                            {/* Return Results Button */}
                            <Row>
                                <Col>
                                    <Button variant="primary" onClick={handleSearchClick} disabled={loadingBookings}>
                                        {loadingBookings ? <Spinner size="sm" className="me-2" /> : null}
                                        Return results
                                    </Button>
                                </Col>
                            </Row>
                        </Card.Body>
                    </Card>
                )}

                {/* Bookings Table */}
                {bookings.length > 0 && (
                    <Card>
                        <Card.Body>
                            <h4 className="mb-3">Bookings</h4>
                            
                            {/* Desktop Table */}
                            <div className="table-responsive">
                                <Table striped hover>
                                    <thead>
                                        <tr>
                                            <th>Title</th>
                                            <th>Date</th>
                                            <th>Units</th>
                                            <th>Type</th>
                                            <th>Expert</th>
                                            <th>Description</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {bookings.map(booking => (
                                            <tr key={booking.id}>
                                                <td>{booking.title}</td>
                                                <td>{booking.date}</td>
                                                <td>{booking.units}</td>
                                                <td className={booking.is_transfer ? 'text-transfer' : ''}>
                                                    {booking.type}
                                                </td>
                                                <td>{booking.expert}</td>
                                                <td>
                                                    {booking.description ? (
                                                        <Button 
                                                            variant="link" 
                                                            size="sm" 
                                                            onClick={() => handleViewDescription(booking)}
                                                            style={{ padding: 0 }}
                                                        >
                                                            View Description
                                                        </Button>
                                                    ) : (
                                                        '-'
                                                    )}
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </Table>
                            </div>

                            {/* Mobile Cards */}
                            <div className="mobile-cards">
                                {bookings.map(booking => (
                                    <div key={booking.id} className="booking-card">
                                        {/* Header */}
                                        <div className="booking-card-header">
                                            <div className="booking-card-title-section">
                                                <h6 className="booking-card-title">{booking.title}</h6>
                                            </div>
                                        </div>
                                        
                                        {/* Body */}
                                        <div className="booking-card-body">
                                            <div className="booking-card-field">
                                                <span className="booking-card-label">Date</span>
                                                <span className="booking-card-value">{booking.date}</span>
                                            </div>
                                            
                                            <div className="booking-card-field">
                                                <span className="booking-card-label">Units</span>
                                                <span className="booking-card-value">{booking.units}</span>
                                            </div>
                                            
                                            <div className="booking-card-field">
                                                <span className="booking-card-label">Type</span>
                                                <span className={`booking-card-value ${booking.is_transfer ? 'text-transfer' : ''}`}>
                                                    {booking.type}
                                                </span>
                                            </div>
                                            
                                            <div className="booking-card-field">
                                                <span className="booking-card-label">Expert</span>
                                                <span className="booking-card-value">{booking.expert}</span>
                                            </div>
                                            
                                            <div className="booking-card-field">
                                                <span className="booking-card-label">Description</span>
                                                <div className="booking-card-value">
                                                    {booking.description ? (
                                                        <Button 
                                                            variant="link" 
                                                            size="sm" 
                                                            onClick={() => handleViewDescription(booking)}
                                                            style={{ padding: 0, textDecoration: 'none' }}
                                                        >
                                                            View Description
                                                        </Button>
                                                    ) : (
                                                        <span>-</span>
                                                    )}
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                ))}
                                
                                {hasMore && (
                                    <div className="text-center mt-3">
                                        <Button 
                                            variant="primary" 
                                            onClick={handleLoadMore}
                                            disabled={loadingBookings}
                                        >
                                            {loadingBookings ? 'Loading...' : 'Show more'}
                                        </Button>
                                    </div>
                                )}
                            </div>
                        </Card.Body>
                    </Card>
                )}

                {hasSearched && !loadingBookings && bookings.length === 0 && (
                    <Alert variant="info" className="mb-4">
                        No bookings found. Try adjusting your filters.
                    </Alert>
                )}

                {/* Description Modal */}
                <Modal show={showModal} onHide={handleCloseModal} size="lg">
                    <Modal.Header closeButton>
                        <Modal.Title>{modalTitle}</Modal.Title>
                    </Modal.Header>
                    <Modal.Body>
                        <div className="theme-text" style={{ whiteSpace: 'pre-wrap' }}>
                            {modalDescription}
                        </div>
                    </Modal.Body>
                    <Modal.Footer>
                        <Button variant="secondary" onClick={handleCloseModal}>
                            Close
                        </Button>
                    </Modal.Footer>
                </Modal>
            </Container>
        </div>
    );
}

export default NetworkConsumptionReport;